import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';
import TableComponent from 'components/common/TableComponent';
import MonitorCard from 'components/Dashboard/MonitorCard';
import StatCards from 'components/Dashboard/StatCards';
import SectionIntro from 'components/common/SectionIntro';
import { makeRequest } from 'services/request.service';
import { useAuth } from 'auth/AuthContext';
import { parseDate } from 'utils';

// GET request for daily agro summary data
const getDailyAgroSummaryData = async (farmId, filterDate) => {
  try {
    const response = await makeRequest({
      method: 'GET',
      url: `/dashboard/${farmId}/agro-summary?filterDate=${filterDate}`,
      headers: {
        Authorization: `Bearer ${localStorage.getItem('secret')}`
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching daily agro summary data:', error);
    throw error;
  }
};

const AgroActionsTable = ({ agroActionsSummary }) => {
  const { t } = useTranslation();
  const tableData = {
    hasCheckbox: false,
    columns: [
      { key: 'blockName', label: t('table.columns.blockName') },
      { key: 'crop', label: t('table.columns.crop') },
      { key: 'variety', label: t('table.columns.variety') },
      { key: 'activityName', label: t('table.columns.activityName') },
      { key: 'units', label: t('table.columns.units') },
      {
        key: 'percentageOfCompletion',
        label: t('table.columns.percentageCompletion'),
        cell: value => `${value}%`
      }
    ],
    rows: agroActionsSummary
  };
  return <TableComponent tableData={tableData} defaultSortColumn={tableData.columns[0]?.key} />;
};

AgroActionsTable.propTypes = {
  agroActionsSummary: PropTypes.arrayOf(PropTypes.object)
};

const WorkersMonitorTable = ({ workersMonitor }) => {
  const { t } = useTranslation();
  const tableData = {
    hasCheckbox: false,
    columns: [
      { key: 'workerId', label: t('table.columns.workerId') },
      { key: 'workerName', label: t('table.columns.workerName') },
      { key: 'activity', label: t('table.columns.activity') },
      { key: 'blockName', label: t('table.columns.blockName') },
      { key: 'totalPlants', label: t('table.columns.units') },
      { key: 'totalHours', label: t('table.columns.totalWorkHours') },
      { key: 'plantsPerHour', label: t('table.columns.avgRatePerHour') },
      { key: 'groupName', label: t('table.columns.groupName') },
      { key: 'firstScan', label: t('table.columns.firstScan'), cell: value => parseDate(value) },
      { key: 'lastScan', label: t('table.columns.lastScan'), cell: value => parseDate(value) }
    ],
    rows: workersMonitor
  };
  return <TableComponent tableData={tableData} defaultSortColumn={tableData.columns[0]?.key} />;
};

WorkersMonitorTable.propTypes = {
  workersMonitor: PropTypes.arrayOf(PropTypes.object)
};

const DailyAgroSummary = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const hasFetched = useRef(false);
  const [agroSummaryData, setAgroSummaryData] = useState({
    dailyAgroSummary: {
      totalActions: 0,
      workerCount: 0,
      averagePerWorker: 0
    },
    agroActionsSummary: [],
    workersMonitor: [],
    loading: true,
    error: null
  });

  // Get current date in Mexico City timezone
  const currentDate = DateTime.now().setZone('America/Mexico_City').toFormat('yyyy-MM-dd');
  const formattedDate = DateTime.now().setZone('America/Mexico_City').toFormat('dd.MM.yyyy');

  // New state for selected date
  const [selectedDate, setSelectedDate] = useState(currentDate);

  const fetchAgroSummaryData = async (dateToFetch = selectedDate) => {
    if (!user?.farmId) return;

    try {
      setAgroSummaryData(prev => ({ ...prev, loading: true, error: null }));

      const data = await getDailyAgroSummaryData(user.farmId, dateToFetch);

      setAgroSummaryData(prev => ({
        ...prev,
        dailyAgroSummary: data.dailyAgroSummary || prev.dailyAgroSummary,
        agroActionsSummary: data.agroActionsSummary || [],
        workersMonitor: data.workersMonitor || [],
        loading: false
      }));
    } catch (error) {
      console.error('Error fetching agro summary:', error);
      setAgroSummaryData(prev => ({
        ...prev,
        loading: false,
        error: 'Failed to load agro summary data'
      }));
    }
  };

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;

    fetchAgroSummaryData(selectedDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.farmId]);

  // Refetch when selectedDate changes
  useEffect(() => {
    if (!hasFetched.current) return;
    fetchAgroSummaryData(selectedDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  const cardData = [
    {
      title: t('dashboard.totalActions'),
      value: agroSummaryData.dailyAgroSummary.totalActions,
      unit: t('common.units'),
      color: 'workspace-stat-card--accent'
    },
    {
      title: t('dashboard.costPerUnit'),
      value: '$0.01',
      unit: t('dashboard.perUnit'),
      color: 'workspace-stat-card--accent-soft'
    },
    {
      title: t('dashboard.workerCount'),
      value: agroSummaryData.dailyAgroSummary.workerCount,
      unit: t('common.workers'),
      color: 'workspace-stat-card--accent'
    },
    {
      title: t('dashboard.avgPerWorker'),
      value: agroSummaryData.dailyAgroSummary.averagePerWorker,
      unit: t('common.units'),
      color: 'workspace-stat-card--accent-soft'
    }
  ];

  return (
    <div className="min-h-screen p-6">
      <SectionIntro
        kicker={t('pageIntro.dashboardKicker')}
        title={t('dashboard.dailyAgroSummary')}
        description={t('pageIntro.dailyAgroSummaryDescription')}
        actions={
          <div className="flex items-center gap-2">
            <label htmlFor="agro-date" className="workspace-summary-date-label text-sm">
              {t('dashboard.selectDate')}
            </label>
            <input
              id="agro-date"
              type="date"
              className="workspace-summary-date-input rounded border px-2 py-1 text-sm"
              value={selectedDate}
              max={currentDate}
              onChange={e => setSelectedDate(e.target.value)}
            />
          </div>
        }
        className="mb-4"
      />

      <hr className="workspace-summary-divider mb-6 border-t" />

      {agroSummaryData.loading ? (
        <div className="workspace-summary-message">{t('dashboard.loadingAgroData')}</div>
      ) : agroSummaryData.error ? (
        <div className="workspace-summary-error">{agroSummaryData.error}</div>
      ) : (
        <>
          <StatCards cards={cardData} />

          <div className="grid gap-4 mt-4">
            <MonitorCard
              title={t('dashboard.agroActionsSummary')}
              date={formattedDate}
              hasData={agroSummaryData.agroActionsSummary.length > 0}
              color="green"
            >
              <AgroActionsTable agroActionsSummary={agroSummaryData.agroActionsSummary} />
            </MonitorCard>

            <MonitorCard
              title={t('dashboard.workersMonitor')}
              date={formattedDate}
              hasData={agroSummaryData.workersMonitor.length > 0}
              color="green"
            >
              <WorkersMonitorTable workersMonitor={agroSummaryData.workersMonitor} />
            </MonitorCard>
          </div>
        </>
      )}
    </div>
  );
};

export default DailyAgroSummary;
